/**
 * Claim detail — one row per billed claim.
 *
 * The rows are a separate artefact (`/api/claims`, see src/data/claimDetail.ts),
 * fetched when this page opens and kept columnar: a row here is an index into those
 * columns, never an object. Job title and shore are the owner's, looked up from the
 * bundle's people rather than repeated on every claim.
 */
import { useEffect, useMemo, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAppData } from '@/data/AppData'
import { useClaimDetail } from '@/data/claimDetail'
import { billedBasis } from '@/data/billedBasis'
import { patientOf } from '@/data/patient'
import { readClaimDrill, type ClaimPickKey } from '@/data/claimDrill'
import { useTouchDetail } from '@/data/touchDetail'
import { useOpenTouchDetail } from '@/data/touchDrill'
import { claimKey, touchesByClaim } from '@/data/touchRows'
import { facilityGroupOf, inGroups } from '@/data/facilityGroups'
import { applyFilters, useFilters } from '@/data/filters'
import { useLoadedDataset } from '@/data/DatasetProvider'
import { ClaimDetailShell } from '@/components/ClaimDetailShell'
import { SearchPick } from '@/components/SearchPick'
import { MultiSelect } from '@/components/MultiSelect'
import { dictOptions, indexOfValue, indexesOf, sameValue } from '@/data/blankValue'
import { HiddenByStatus } from '@/components/HiddenByStatus'
import type { Column } from '@/components/Table'
import { linked } from '@/components/FacilityLink'
import type { ExportColumn } from '@/ui/export'
import { int, money0, money2, moneyK, monthRuns, monthSpan, monthYear } from '@/format'
import { useUrlState, text, json, bool, list } from '@/ui/urlState'
import { DIMENSION_HELP, type Definition } from '@/measures/definitions'
import { indexAdapter, pickAdapter } from '@/state/dictShare'
import { attributionOf, hasAttribution, rowOfClaim } from '@/data/attribution'
import { assignmentsLink } from '@/data/assignments'
import { ClaimAttribution } from '@/components/ClaimAttribution'
import { usePageAllowed, usePageLimit } from '@/access/pageLimit'

/**
 * Her six dropdowns, in her order, plus service type (2026-09-23: "claim type and
 * service type" in place of included / excluded). Each holds a dictionary index, ''
 * for All. Facility group is not a claim column and has its own picker below.
 */
const PICKERS = [
  { key: 'e', label: 'Employee' },
  { key: 'f', label: 'Facility' },
  { key: 's', label: 'State' },
  { key: 'p', label: 'Payer type', help: DIMENSION_HELP.payerType },
  { key: 'st', label: 'Status' },
  { key: 'ty', label: 'Claim type', help: DIMENSION_HELP.claimType },
  { key: 'sv', label: 'Service type', help: DIMENSION_HELP.serviceType },
] as const satisfies readonly { key: string; label: string; help?: Definition }[]
type PickerKey = (typeof PICKERS)[number]['key']
const NONE: Record<PickerKey, string> = { e: '', f: '', s: '', p: '', st: '', ty: '', sv: '' }

/** Bill dates inside the top bar's month range, as dictionary indexes. */
function monthsAllowed(dates: string[], months: string[]): Set<number> {
  const inRange = new Set(months)
  return new Set(dates.flatMap((d, i) => (inRange.has(d.slice(0, 7)) ? [i] : [])))
}

/** Every index of `dict` whose value is one of `months`. */
const indexesIn = (dict: readonly string[], months: readonly string[]) => {
  const want = new Set(months)
  return dict.flatMap((m, i) => (want.has(m) ? [i] : []))
}

/** "2026-03-01" -> "3/1/2026", as the Ledger shows it. */
const usDate = (v: string) =>
  /^\d{4}-\d{2}-\d{2}/.test(v) ? `${+v.slice(5, 7)}/${+v.slice(8, 10)}/${v.slice(0, 4)}` : v

/** A month picker's selection, once each and in order, so its link reads one way. */
const tidyMonths = (months: readonly string[]) => [...new Set(months)].filter(Boolean).sort()
/** `months` with `m` added, or taken out if it was there. */
const toggleMonth = (months: readonly string[], m: string) =>
  tidyMonths(months.includes(m) ? months.filter((x) => x !== m) : [...months, m])

export function ClaimDetail() {
  const { scope, derived } = useAppData()
  const { filters, restore } = useFilters()
  const dataset = useLoadedDataset()

  // Whose claims: the top bar's PERSON filters only. Its claim status / type / service
  // type and facility group reweight people by a share and drop anyone whose share is
  // zero — including people who do own such claims. Here the claims themselves are
  // filtered exactly below, so the share is left out of choosing people.
  const owners = useMemo(
    () =>
      new Set(
        applyFilters(derived, dataset.composition, {
          ...filters,
          claimType: [],
          serviceType: [],
          claimStatus: [],
          facilityGroup: [],
        }).people.map((p) => p.employee),
      ),
    [derived, dataset.composition, filters],
  )
  // The same owners with Employee status set to both. A claim whose owner is here but
  // not above is hidden by that control alone, and is counted so the page can say so.
  const ownersAnyStatus = useMemo(
    () =>
      filters.employmentStatus === 'both'
        ? owners
        : new Set(
            applyFilters(derived, dataset.composition, {
              ...filters,
              employmentStatus: 'both',
              claimType: [],
              serviceType: [],
              claimStatus: [],
              facilityGroup: [],
            }).people.map((p) => p.employee),
          ),
    [owners, derived, dataset.composition, filters],
  )
  const { data, error } = useClaimDetail()
  /** The claim whose attribution panel is open (reviews #394, #405), by row index. */
  const [why, setWhy] = useState<number | null>(null)
  /*
   * Each claim's touches (review #165: "add touches here and if click the # of touches
   * takes you directly to the corresponding touches"). Counted off the very rows Touch
   * detail lists — by visit + payer, any date, by anyone (touchRows.ts) — so the number
   * clicked is the number of rows it opens. Null until the touch rows arrive.
   */
  // A page-limited reader without Touch detail (access/pageLimit.tsx): no touch rows, and
  // no column whose clicks lead there. Without Worklist assignments, worklists are text.
  const withTouches = usePageAllowed('/touch-detail')
  const withAssignments = usePageAllowed('/worklist-assignments')
  // A page-limited reader is served no labor (api/shared/pages.ts claimsForPages): it is
  // pay spread over the claims, so no column, total or export of it.
  const withLabor = usePageLimit() === null
  const touchFile = useTouchDetail(withTouches)
  const touchCount = useMemo(() => {
    if (!touchFile.data) return null
    const byClaim = touchesByClaim(touchFile.data)
    return (visit: string, payer: string) =>
      visit ? (byClaim.get(claimKey(visit, payer))?.length ?? 0) : null
  }, [touchFile.data])
  const openTouches = useOpenTouchDetail()
  /*
   * Kept for Back (ui/urlState.ts). The dropdowns, search, team and department in this
   * history entry — the Employee dropdown and the team name people — and the facility
   * group, which names nobody, in the URL.
   */
  // A shared link carries the dropdowns as values, not this caller's indexes (state/dictShare.ts).
  const pickDicts = useMemo(
    () => (data ? Object.fromEntries(PICKERS.map((k) => [k.key, data.dict[k.key] as readonly string[] | undefined])) : null),
    [data],
  )
  const [picked, setPicked] = useUrlState('pick', NONE, json(NONE), { personal: true, share: pickAdapter(pickDicts) })
  /** Facility group, by name; '' for All. */
  const [group, setGroup] = useUrlState('group', '', text)

  /*
   * Each facility's group, read off the bundle's crosstab — the claim rows carry the
   * facility but not its group, and the group is what the Overview slices on. A
   * facility filed under more than one group takes the one most of its claims sit in.
   */
  const groupOf = useMemo(() => facilityGroupOf(dataset.crosstab), [dataset.crosstab])
  // A blank group, by any spelling, is the one "Unknown" the Overview draws (#177).
  const groups = useMemo(() => [...new Set(groupOf.values())].sort(), [groupOf])
  // Free text can hold anything (a patient's name): never put in a shared link.
  const [q, setQ] = useUrlState('q', '', text, { personal: true, share: false })
  // What is in the box as it is typed; `q`, the search applied, follows it after a pause.
  const [typed, setTyped] = useState(q)
  /**
   * A visit the header Search opened (#604): searched for, and shown even where the
   * filters would hide it. Cleared once done, so Back to this entry keeps whatever was
   * changed since. In the entry only, like the search itself.
   */
  const [reveal, setReveal] = useUrlState('reveal', '', text, { personal: true, share: false })
  // Arriving from the header while already here: the box shows the new search.
  useEffect(() => {
    if (reveal) setTyped(reveal)
  }, [reveal])
  /** A team handed over by another tab's "Claims" button; see claimDrill.ts. */
  const [teamRaw, setTeamRaw] = useUrlState(
    'team',
    null as { names: string[]; label: string } | null,
    json<{ names: string[]; label: string } | null>(null),
    { personal: true },
  )
  const team = useMemo(
    () => (teamRaw ? { names: new Set(teamRaw.names), label: teamRaw.label } : null),
    [teamRaw],
  )
  const setTeam = (t: { names: Set<string>; label: string } | null) =>
    setTeamRaw(t ? { names: [...t.names], label: t.label } : null)
  /** A department handed over by an Overview slice, as a dictionary index. */
  const [dept, setDept] = useUrlState('dept', null as number | null, json<number | null>(null), {
    personal: true,
    share: indexAdapter(data?.dict.dept, !!data),
  })
  /** The worker's home department an Overview department bar handed over, by `dict.wd`. */
  const [home, setHome] = useUrlState('home', null as number | null, json<number | null>(null), {
    personal: true,
    share: indexAdapter(data?.dict.wd, !!data),
  })
  /**
   * Bill months a drill asked for (review #227): one month's cell on the person page,
   * or a collection rate's window, which reaches back before the range. In place of
   * the top bar's range while set; a removable tag. Names nobody, so it is in the URL.
   */
  const [only, setOnly] = useUrlState('only', [] as string[], list)
  const billMonths = useMemo(() => (only.length ? only : scope.months), [only, scope.months])
  /**
   * Resolution months (review #397): the claims resolved in them, billed any month —
   * what resolved claims, 0% touch share and touches per touched claim count. While set
   * the top bar's range no longer limits the bill date (a "Billed in" tag still does),
   * and the claims billed outside the period or in a month their owner was not paid,
   * which only that basis counts, are listed too. Names nobody, so it is in the URL.
   */
  const [resolvedIn, setResolvedIn] = useUrlState('rin', [] as string[], list)
  // A file built before the resolved basis has no resolution month to filter on.
  const byResolution = resolvedIn.length > 0 && !!data?.dict.rm && !!data.cols.rm
  /**
   * Exactly these claims, by facility + visit: a Touch drivers count (claimDrill.ts).
   * Visits are a patient's: kept in the history entry, never in a link.
   */
  const [visits, setVisits] = useUrlState(
    'visits',
    null as { label: string; keys: string[] } | null,
    json<{ label: string; keys: string[] } | null>(null),
    { personal: true, share: false },
  )
  /** Whether this entry's drill has been applied; see below. */
  const [drilled, setDrilled] = useUrlState('drilled', false, bool, { personal: true })

  // Arriving from a "Claims" button: start from clean filters, then narrow to what it
  // carried — one person in the Employee dropdown, several as a team tag. Once per
  // history entry, and only once the rows (and so the dropdown's values) are here.
  // Recorded IN the entry, so coming Back to it keeps whatever was picked since rather
  // than applying the drill over the top again.
  const location = useLocation()
  useEffect(() => {
    const drill = readClaimDrill(location.state)
    if (!drill || !data || drilled) return
    setDrilled(true)
    setTyped('')
    setQ('')
    setDept(null)
    setHome(null)
    setGroup('')
    setVisits(null)
    if (drill.visits) {
      // The claims themselves: no person, month or status narrows them.
      setVisits({ label: drill.label, keys: drill.visits.map((c) => `${c.f}${c.v}`) })
      setResolvedIn([])
      setOnly([])
      setPicked(NONE)
      setTeam(null)
      return
    }
    // The resolved basis by its own month (review #397). A file without resolution
    // months gets the old reading instead: resolved claims billed in those months.
    const byRes = !!drill.resolvedIn?.length && !!data.dict.rm
    setResolvedIn(byRes ? drill.resolvedIn! : [])
    setOnly(drill.months ?? (drill.resolvedIn && !byRes ? drill.resolvedIn : []))
    // The claim status a measure counts (review #227: 0% touch share reads resolved
    // claims), in the Status dropdown, alongside whoever the drill names.
    const status = drill.status ?? (drill.resolvedIn && !byRes ? 'Resolved' : undefined)
    const st = status ? indexOfValue(data.dict.st, status) : -1
    const withStatus = st >= 0 ? { st: String(st) } : {}
    // An Overview slice the rows carry goes in its own dropdown, as hers does. Facility
    // group is by name, not a dictionary index: the rows don't carry it (groupOf above).
    // Matched blank-for-blank, so the "Unknown" bar finds "", "Unknown" and "UNKNOWN"
    // alike (review #177).
    const { fg, ...byIndex } = drill.pick ?? {}
    const pick = Object.entries(byIndex).map(([k, v]) => {
      const dict = data.dict[k as Exclude<ClaimPickKey, 'fg'>]
      return [k, dict ? indexOfValue(dict, v ?? '') : -1] as const
    })
    const fgName = fg === undefined ? undefined : groups.find((g) => sameValue(g, fg))
    const fgOk = fg === undefined || fgName !== undefined
    if ((pick.length || fg !== undefined) && fgOk && pick.every(([, i]) => i >= 0)) {
      const dropdowns = pick.filter(([k]) => k !== 'dept' && k !== 'wd')
      setPicked({
        ...NONE,
        ...withStatus,
        ...Object.fromEntries(dropdowns.map(([k, i]) => [k, String(i)])),
      })
      setDept(pick.find(([k]) => k === 'dept')?.[1] ?? null)
      setHome(pick.find(([k]) => k === 'wd')?.[1] ?? null)
      setGroup(fgName ?? '')
      setTeam(null)
      return
    }
    const one = drill.employees.length === 1 ? data.dict.e.indexOf(drill.employees[0]!) : -1
    setPicked(one >= 0 ? { ...NONE, ...withStatus, e: String(one) } : { ...NONE, ...withStatus })
    setTeam(one >= 0 ? null : { names: new Set(drill.employees), label: drill.label })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, data, drilled])

  // Search waits for a pause in typing, as hers does; each pass scans every claim.
  useEffect(() => {
    const t = setTimeout(() => setQ(typed.trim().toLowerCase()), 250)
    return () => clearTimeout(t)
  }, [typed])

  const { rows, hidden, outside, missing } = useMemo(() => {
    if (!data) return { rows: [], hidden: 0, outside: [] as number[], missing: 0 }
    const { dict, cols } = data
    if (visits) {
      // Every row of these claims the caller may see, any month, anyone's; only the
      // dropdowns and the search narrow them. Claims not on file are counted, not lost.
      const want = new Set(visits.keys)
      const found = new Set<string>()
      const same = (d: readonly string[] | undefined, i: number) =>
        new Set(d && d[i] !== undefined ? indexesOf(d, d[i]!) : [i])
      const active = PICKERS.filter((k) => picked[k.key] !== '' && cols[k.key]).map((k) => ({
        col: cols[k.key]!,
        want: same(dict[k.key], Number(picked[k.key])),
      }))
      const out: number[] = []
      for (let i = 0; i < data.n; i++) {
        const key = `${dict.f[cols.f[i]!]}${cols.v[i]}`
        if (!want.has(key)) continue
        found.add(key)
        if (active.some((a) => !a.want.has(a.col[i]!))) continue
        if (q && !(cols.v[i]!.toLowerCase().includes(q) || dict.f[cols.f[i]!]!.toLowerCase().includes(q) || dict.e[cols.e[i]!]!.toLowerCase().includes(q)))
          continue
        out.push(i)
      }
      return { rows: out, hidden: 0, outside: [] as number[], missing: want.size - found.size }
    }
    // The top bar's claim-level settings. Elsewhere they reweight people by share;
    // here each claim carries its own status, type, service type and bill month, so
    // they apply exactly. Each becomes the set of dictionary indexes it allows.
    const allow = (values: string[] | undefined, sel: string[]) =>
      sel.length === 0 || !values
        ? null
        : new Set(values.flatMap((v, i) => (sel.includes(v) ? [i] : [])))
    const bar = [
      { col: cols.st, ok: allow(dict.st, filters.claimStatus) },
      { col: cols.ty, ok: allow(dict.ty, filters.claimType) },
      { col: cols.sv, ok: allow(dict.sv, filters.serviceType) },
      // By resolution month, the bill date limits only through a "Billed in" tag.
      ...(byResolution && !only.length ? [] : [{ col: cols.d, ok: monthsAllowed(dict.d, billMonths) }]),
      ...(byResolution ? [{ col: cols.rm, ok: new Set(indexesIn(dict.rm!, resolvedIn)) }] : []),
    ].filter((b): b is { col: number[]; ok: Set<number> } => !!b.col && !!b.ok)
    // A dropdown's pick, as every index holding its value: "Unknown" is each blank
    // spelling at once (review #177).
    const same = (d: readonly string[] | undefined, i: number) =>
      new Set(d && d[i] !== undefined ? indexesOf(d, d[i]!) : [i])
    const active = PICKERS.filter((k) => picked[k.key] !== '' && cols[k.key]).map((k) => ({
      col: cols[k.key]!,
      want: same(dict[k.key], Number(picked[k.key])),
    }))
    const deptOk = dept === null ? null : same(dict.dept, dept)
    const homeOk = home === null ? null : same(dict.wd, home)
    // The top bar's Overview slice, where it carries the bar (Filters.slice): its claims
    // only, not every claim of everyone who works it.
    const sm = filters.slice?.match
    const sliceCols = sm
      ? [
          { col: cols.s, ok: sm.state ? new Set(indexesOf(dict.s, sm.state)) : null },
          { col: cols.p, ok: sm.payer ? new Set(indexesOf(dict.p, sm.payer)) : null },
          { col: cols.f, ok: sm.facility ? new Set(indexesOf(dict.f, sm.facility)) : null },
          {
            col: cols.wd,
            ok: sm.department && dict.wd ? new Set(indexesOf(dict.wd, sm.department)) : null,
          },
        ].filter((b): b is { col: number[]; ok: Set<number> } => !!b.col && !!b.ok)
      : []
    const sliceGroup = sm?.facilityGroup ?? null
    // The top bar's Facility group filter, on the claim's facility: exact.
    const barGroups = filters.facilityGroup
    const inBarGroups = (i: number) => inGroups(groupOf.get(dict.f[cols.f[i]!]!), barGroups)
    // Visit, facility, payer, employee and patient — the columns her search reads.
    const hit = (i: number) =>
      cols.v[i]!.toLowerCase().includes(q) ||
      dict.f[cols.f[i]!]!.toLowerCase().includes(q) ||
      dict.pn[cols.pn[i]!]!.toLowerCase().includes(q) ||
      dict.e[cols.e[i]!]!.toLowerCase().includes(q) ||
      (!!cols.pt && !!dict.pt && dict.pt[cols.pt[i]!]!.toLowerCase().includes(q))
    const out: number[] = []
    let hidden = 0
    for (let i = 0; i < data.n; i++) {
      const owner = dict.e[cols.e[i]!]!
      if (!ownersAnyStatus.has(owner)) continue
      // Resolved-basis-only claims are not billed claims of the period (claimDetail.ts).
      if (!byResolution && !billedBasis(cols, i)) continue
      if (team && !team.names.has(owner)) continue
      if (deptOk && !deptOk.has(cols.dept[i]!)) continue
      if (homeOk && !homeOk.has(cols.wd?.[i] ?? -1)) continue
      if (group && !sameValue(groupOf.get(dict.f[cols.f[i]!]!), group)) continue
      if (sliceGroup && !sameValue(groupOf.get(dict.f[cols.f[i]!]!), sliceGroup)) continue
      if (barGroups.length && !inBarGroups(i)) continue
      if (bar.some((b) => !b.ok.has(b.col[i]!))) continue
      if (sliceCols.some((b) => !b.ok.has(b.col[i]!))) continue
      if (active.some((a) => !a.want.has(a.col[i]!))) continue
      if (q && !hit(i)) continue
      if (owners.has(owner)) out.push(i)
      else hidden++
    }
    // A search that finds nothing inside the filters: the matches among every row the
    // caller may see (/api/claims scoped the file, so this widens nothing), for the
    // page to offer. Yehuda, 2026-10-14: visit 4252996, searched while narrowed to
    // another biller, was in the file, credited to someone else and billed in August.
    const outside: number[] = []
    if (q && out.length === 0 && hidden === 0)
      for (let i = 0; i < data.n; i++) if (billedBasis(cols, i) && hit(i)) outside.push(i)
    return { rows: out, hidden, outside, missing: 0 }
  }, [data, visits, owners, ownersAnyStatus, billMonths, only, byResolution, resolvedIn, filters, team, dept, home, group, groupOf, picked, q])

  const labor = useMemo(() => (data ? rows.reduce((s, i) => s + data.cols.lab[i]!, 0) : 0), [data, rows])

  /**
   * The months the two month pickers offer. Bill months are those of the billed claims
   * (paid months only, billedBasis.ts) — by resolution month, of every claim listed, as
   * the bill date then reaches any month.
   */
  const billOptions = useMemo(() => {
    if (!data) return []
    const { dict, cols } = data
    const seen = new Set<number>()
    for (let i = 0; i < data.n; i++) if (byResolution || billedBasis(cols, i)) seen.add(cols.d[i]!)
    return tidyMonths([...seen].map((d) => (dict.d[d] ?? '').slice(0, 7)))
  }, [data, byResolution])
  const resolvedOptions = useMemo(() => tidyMonths(data?.dict.rm ?? []), [data])

  /**
   * Show a search's matches outside the filters: every filter back to all (the top
   * bar's too, with Employee status both), the search kept, and the bill months the
   * matches were billed in as a "Billed in" tag, in place of the top bar's range. All
   * of it in the URL and the history entry, as the controls themselves keep it.
   */
  const showOutside = () => {
    if (!data || !outside.length) return
    const { dict, cols } = data
    const months = [...new Set(outside.map((i) => (dict.d[cols.d[i]!] ?? '').slice(0, 7)))].filter(Boolean).sort()
    restore({ employmentStatus: 'both' })
    setPicked(NONE)
    setTeam(null)
    setDept(null)
    setHome(null)
    setGroup('')
    setResolvedIn([])
    setOnly(months)
  }
  /*
   * The header Search's visit (#604). Shown already: nothing to do. Hidden by the
   * filters: as "Not in your current filters" does (#207), every filter back to all and
   * the months it was billed in, or, for a claim only on the resolved basis, the months
   * it resolved in. Not among the caller's rows at all: the search says so.
   */
  useEffect(() => {
    if (!reveal || !data || q !== reveal.toLowerCase()) return
    const { dict, cols } = data
    setReveal('')
    if (rows.some((i) => cols.v[i] === reveal)) return
    const hits: number[] = []
    for (let i = 0; i < data.n; i++) if (cols.v[i] === reveal) hits.push(i)
    if (!hits.length) return
    const billed = hits.filter((i) => billedBasis(cols, i))
    const monthsOf = (col: number[], d: readonly string[], at: number[]) =>
      [...new Set(at.map((i) => (d[col[i]!] ?? '').slice(0, 7)))].filter(Boolean).sort()
    restore({ employmentStatus: 'both' })
    setPicked(NONE)
    setTeam(null)
    setDept(null)
    setHome(null)
    setGroup('')
    if (billed.length || !cols.rm || !dict.rm) {
      setResolvedIn([])
      setOnly(monthsOf(cols.d, dict.d, billed.length ? billed : hits))
    } else {
      setOnly([])
      setResolvedIn(monthsOf(cols.rm, dict.rm, hits))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reveal, data, q, rows])

  // The rows' weighted count, when they carry weights: Σ weight over the rows shown.
  const weighted = useMemo(() => {
    const w = data?.cols.w
    return w ? rows.reduce((s, i) => s + (w[i] ?? 1), 0) : null
  }, [data, rows])

  const columns = useMemo<Column<number>[]>(() => {
    if (!data) return []
    const { dict, cols } = data
    const txt = (key: Exclude<keyof typeof dict, 'sv' | 'pt' | 'wr' | 'cb' | 'wd' | 'rm' | 'pw' | 'pu' | 'ab' | 'ot' | 'as' | 'cr' | 'cp' | 'ep'>) => (i: number) => dict[key][cols[key][i]!] ?? ''
    const person = (i: number) => derived.byName.get(dict.e[cols.e[i]!]!)
    const patient = patientOf(data)
    const mono = (s: string) => <span className="mono" style={{ fontSize: 'var(--t-table)' }}>{s}</span>
    const col = (
      key: string,
      header: string,
      get: (i: number) => string,
      width?: number,
      help?: Definition,
    ): Column<number> => ({ key, header, width, sort: get, render: get, help })
    return [
      col('e', 'Employee', txt('e'), 170),
      col('dept', 'Department', txt('dept')),
      // Whose department the claim counts under on the Overview (build_bundle.py,
      // home_department): the owner's that month, not the claim's.
      ...(cols.wd && dict.wd ? [col('wd', 'Home department', (i) => dict.wd![cols.wd![i]!] ?? '')] : []),
      col('jt', 'Job title', (i) => person(i)?.jobTitle ?? ''),
      // Both open on Facilities (review #226).
      linked(col('fg', 'Facility group', (i) => groupOf.get(dict.f[cols.f[i]!]!) ?? '', 180), 'group'),
      linked(col('f', 'Facility', txt('f'), 220), 'facility'),
      col('s', 'State', txt('s')),
      // Where Shayna pointed (review #69). Only once the build carries names.
      ...(patient ? [col('pt', 'Patient', patient, 180)] : []),
      col('p', 'Payer type', txt('p'), undefined, DIMENSION_HELP.payerType),
      col('pn', 'Payer name', txt('pn'), undefined, DIMENSION_HELP.payerName),
      // What the claim counts as under the claim rules, and why (docs/RULES.md). Shown
      // whichever way the Weighted claims switch is set, so a weighted score can be
      // traced back to its claims.
      ...(cols.w
        ? [
            {
              key: 'w',
              header: 'Weight',
              align: 'right' as const,
              sort: (i: number) => cols.w![i] ?? 1,
              render: (i: number) => {
                const why = (cols.wr && dict.wr?.[cols.wr[i]!]) || ''
                return (
                  <span className="mono" title={why || 'No rule applies: counts 1'} style={{ fontSize: 'var(--t-table)', ...(cols.w![i] === 1 ? { color: 'var(--ink-3)' } : {}) }}>
                    ×{cols.w![i]}
                  </span>
                )
              },
            },
            col('wr', 'Weighted because', (i) => (cols.wr && dict.wr?.[cols.wr[i]!]) || '', 220),
          ]
        : []),
      // The biller the model credits the claim to (docs/RULES.md), where it says.
      ...(cols.cb && dict.cb ? [col('cb', 'Credited to', (i) => dict.cb![cols.cb![i]!] ?? '')] : []),
      { key: 'v', header: 'Visit', sort: (i) => cols.v[i]!, render: (i) => mono(cols.v[i]!) },
      ...(withTouches ? [{
        key: 'tc',
        header: 'Touches',
        align: 'right' as const,
        title: 'Every touch on this claim (its visit and payer), any date, by anyone. Click to see them.',
        sort: (i: number) => touchCount?.(cols.v[i]!, txt('pn')(i)) ?? null,
        render: (i: number) => {
          if (!touchCount) return mono(touchFile.error ? '—' : '…')
          const visit = cols.v[i]!
          const payer = txt('pn')(i)
          const n = touchCount(visit, payer)
          if (n === null) return mono('—')
          if (n === 0) return <span className="mono" style={{ fontSize: 'var(--t-table)', color: 'var(--ink-3)' }}>0</span>
          return (
            <button
              type="button"
              className="cell-link mono"
              data-testid="claim-touches"
              style={{ fontSize: 'var(--t-table)' }}
              title={`Show the ${int(n)} touches on visit ${visit}`}
              onClick={() => openTouches({ employees: [], label: `visit ${visit}`, claim: { visit, payer } })}
            >
              {int(n)}
            </button>
          )
        },
      }] : []),
      { key: 'd', header: 'Bill date', sort: txt('d'), render: (i) => mono(usDate(txt('d')(i))) },
      col('st', 'Status', txt('st')),
      // The month the claim counts in on the resolved basis (review #397), where the
      // file says; blank when it is not resolved in a month its owner was paid.
      ...(cols.rm && dict.rm
        ? [col('rm', 'Resolved in', (i) => (dict.rm![cols.rm![i]!] ? monthYear(dict.rm![cols.rm![i]!]!) : ''))]
        : []),
      col('ty', 'Claim type', txt('ty'), undefined, DIMENSION_HELP.claimType),
      col(
        'sv',
        'Service type',
        (i) => (cols.sv && dict.sv ? (dict.sv[cols.sv[i]!] ?? '') : ''),
        undefined,
        DIMENSION_HELP.serviceType,
      ),
      {
        key: 'sh',
        header: 'Shore',
        sort: (i) => person(i)?.shore ?? null,
        render: (i) => {
          const s = person(i)?.shore
          return s === 'On' ? 'On shore' : s === 'Off' ? 'Off shore' : '—'
        },
      },
      ...(withLabor
        ? [{ key: 'lab', header: 'Labor $', align: 'right' as const, sort: (i: number) => cols.lab[i]!, render: (i: number) => mono(money2(cols.lab[i]!)) }]
        : []),
      { key: 'chg', header: 'Charge', align: 'right', sort: (i) => cols.chg[i] ?? null, render: (i) => mono(money0(cols.chg[i] ?? null)) },
      { key: 'rem', header: 'Remaining', align: 'right', sort: (i) => cols.rem[i] ?? null, render: (i) => mono(money0(cols.rem[i] ?? null)) },
      // "Included / Excluded" is gone (2026-09-23): it named a modelling flag, not
      // anything about the claim. Claim type and service type say what it is.
      // Last, where Shayna asked for it (review #405): the predicted worklist, opening
      // Worklist assignments at that worklist and the bill month, and ⓘ for the claim's
      // whole attribution path (review #394). Only on a build that carries it.
      ...(hasAttribution(data)
        ? [
            {
              key: 'pw',
              header: 'Predicted worklist',
              width: 300,
              title:
                'The worklist the matcher predicted for this claim at the end of its bill month. Click it for who was assigned to it then; ⓘ for how the claim reached its owner.',
              sort: (i: number) => dict.pw![cols.pw![i]!] || null,
              render: (i: number) => {
                const name = dict.pw![cols.pw![i]!] ?? ''
                const id = cols.pwk?.[i] ?? null
                return (
                  <span style={{ display: 'inline-flex', alignItems: 'baseline' }}>
                    <button
                      type="button"
                      className="ca-info"
                      data-testid="claim-attribution"
                      title="How this claim was attributed"
                      aria-label={`How visit ${cols.v[i]} was attributed`}
                      onClick={() => setWhy(i)}
                    >
                      i
                    </button>
                    {!name ? (
                      <span style={{ color: 'var(--ink-3)' }}>none</span>
                    ) : id === null || !withAssignments ? (
                      <span>{name}</span>
                    ) : (
                      <Link
                        to={assignmentsLink(id, (dict.d[cols.d[i]!] ?? '').slice(0, 7))}
                        className="cell-link"
                        data-testid="claim-worklist"
                        title="Who was assigned to this worklist in the claim's bill month"
                      >
                        {name}
                      </Link>
                    )}
                  </span>
                )
              },
            },
          ]
        : []),
    ]
  }, [data, derived, groupOf, touchCount, touchFile.error, openTouches, withTouches, withAssignments, withLabor])

  const exportColumns = useMemo<ExportColumn<number>[]>(() => {
    if (!data) return []
    const { dict, cols } = data
    const person = (i: number) => derived.byName.get(dict.e[cols.e[i]!]!)
    const patient = patientOf(data)
    return [
      { header: 'Employee', value: (i) => dict.e[cols.e[i]!] ?? '' },
      { header: 'Department', value: (i) => dict.dept[cols.dept[i]!] ?? '' },
      ...(cols.wd && dict.wd ? [{ header: 'Home department', value: (i: number) => dict.wd![cols.wd![i]!] ?? '' }] : []),
      { header: 'Job title', value: (i) => person(i)?.jobTitle ?? '' },
      { header: 'Facility group', value: (i) => groupOf.get(dict.f[cols.f[i]!]!) ?? '' },
      { header: 'Facility', value: (i) => dict.f[cols.f[i]!] ?? '' },
      { header: 'State', value: (i) => dict.s[cols.s[i]!] ?? '' },
      ...(patient ? [{ header: 'Patient', value: patient }] : []),
      { header: 'Payer type', value: (i) => dict.p[cols.p[i]!] ?? '' },
      { header: 'Payer name', value: (i) => dict.pn[cols.pn[i]!] ?? '' },
      ...(cols.w
        ? [
            { header: 'Weight', value: (i: number) => cols.w![i] ?? 1 },
            { header: 'Weighted because', value: (i: number) => (cols.wr && dict.wr?.[cols.wr[i]!]) || '' },
          ]
        : []),
      ...(cols.cb && dict.cb ? [{ header: 'Credited to', value: (i: number) => dict.cb![cols.cb![i]!] ?? '' }] : []),
      { header: 'Visit', value: (i) => cols.v[i] ?? '' },
      ...(touchCount
        ? [{ header: 'Touches', value: (i: number) => touchCount(cols.v[i] ?? '', dict.pn[cols.pn[i]!] ?? '') }]
        : []),
      { header: 'Bill date', value: (i) => dict.d[cols.d[i]!] ?? '' },
      { header: 'Status', value: (i) => dict.st[cols.st[i]!] ?? '' },
      ...(cols.rm && dict.rm ? [{ header: 'Resolved in', value: (i: number) => dict.rm![cols.rm![i]!] ?? '' }] : []),
      { header: 'Claim type', value: (i) => dict.ty[cols.ty[i]!] ?? '' },
      { header: 'Service type', value: (i) => (cols.sv && dict.sv ? (dict.sv[cols.sv[i]!] ?? '') : '') },
      { header: 'Shore', value: (i) => person(i)?.shore ?? '' },
      ...(withLabor ? [{ header: 'Labor $', value: (i: number) => cols.lab[i] ?? null }] : []),
      { header: 'Charge', value: (i) => cols.chg[i] ?? null },
      { header: 'Remaining', value: (i) => cols.rem[i] ?? null },
      ...(hasAttribution(data)
        ? [
            { header: 'Predicted worklist', value: (i: number) => dict.pw![cols.pw![i]!] ?? '' },
            { header: 'Predicted person', value: (i: number) => (cols.pu && dict.pu ? (dict.pu[cols.pu[i]!] ?? '') : '') },
            { header: 'Attribution rule', value: (i: number) => (cols.ab && dict.ab ? (dict.ab[cols.ab[i]!] ?? '') : '') },
            ...(cols.cr && dict.cr
              ? [
                  { header: 'Co-insurance rule', value: (i: number) => dict.cr![cols.cr![i]!] ?? '' },
                  { header: 'Primary claim', value: (i: number) => (cols.cp && dict.cp ? (dict.cp[cols.cp[i]!] ?? '') : '') },
                ]
              : []),
          ]
        : []),
    ]
  }, [data, derived, groupOf, touchCount, withLabor])

  return (
    <>
    {why !== null && data && (
      <ClaimAttribution
        key={why}
        claim={attributionOf(data, why)}
        visit={data.cols.v[why] ?? ''}
        facility={data.dict.f[data.cols.f[why]!] ?? ''}
        facilityId={data.cols.fid?.[why] ?? null}
        onOpenPrimary={(() => {
          // A co-insurance claim's primary, where it is among the caller's rows: its path.
          const id = attributionOf(data, why).primaryClaim
          const row = id ? rowOfClaim(data, id) : null
          return row === null ? null : () => setWhy(row)
        })()}
        onClose={() => setWhy(null)}
      />
    )}
    <ClaimDetailShell
      title="Claim detail"
      grain="one row per billed claim"
      filters={
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px 14px', alignItems: 'flex-end' }}>
          {/*
            Every dropdown searchable, as the Employee one has been since #65 (review
            #233, 2026-10-08: "can we add dynamic search to these filters"). Blank
            spellings are one "Unknown" option, and picking it matches all of them.
          */}
          <SearchPick
            label="Facility group"
            value={group}
            allValue=""
            allLabel="All"
            options={groups.map((g) => ({ value: g, label: g }))}
            onChange={setGroup}
            minWidth={200}
          />
          {PICKERS.filter(({ key }) => !data || data.dict[key]).map((pk) => {
            const { key, label } = pk
            const dict = data?.dict[key] ?? []
            // A pick on a blank spelling the folded list does not show reads as its "Unknown".
            const shownAs = (v: string) => {
              if (v === '' || !dict[Number(v)]) return v
              const i = indexOfValue(dict, dict[Number(v)]!)
              return i >= 0 ? String(i) : v
            }
            return (
              <SearchPick
                key={key}
                label={label}
                help={'help' in pk ? pk.help : undefined}
                value={shownAs(picked[key])}
                allValue=""
                allLabel="All"
                options={dictOptions(dict)}
                onChange={(v) => setPicked((p) => ({ ...p, [key]: v }))}
                minWidth={key === 'e' || key === 'f' ? 200 : 130}
              />
            )
          })}
          {/*
            Any months, gaps and all (review #696, Shayna 2026-10-16: "need to be able to
            choose multiple date ranges here"). Billed in stands in for the top bar's range
            while set, as a drill's months always have; Resolved in is the resolved basis
            (review #397). Both live in the URL as '|'-joined months, so a link with one
            month, from before either took more, opens as it did.
          */}
          {billOptions.length > 0 && (
            <div style={{ minWidth: 150 }} data-testid="claim-billed-in-pick">
              <MultiSelect
                label="Billed in"
                options={billOptions}
                value={only}
                labelOf={monthYear}
                allLabel={byResolution ? 'Any month' : `${monthSpan(scope.months)} (top bar)`}
                onToggle={(m) => setOnly(toggleMonth(only, m))}
                onSet={(ms) => setOnly(tidyMonths(ms))}
              />
            </div>
          )}
          {data?.dict.rm && (
            <div style={{ minWidth: 150 }} data-testid="claim-resolved-in-pick">
              <MultiSelect
                label="Resolved in"
                options={resolvedOptions}
                value={resolvedIn}
                labelOf={monthYear}
                allLabel="Any month"
                onToggle={(m) => setResolvedIn(toggleMonth(resolvedIn, m))}
                onSet={(ms) => setResolvedIn(tidyMonths(ms))}
              />
            </div>
          )}
          <label style={{ display: 'grid', gap: 4 }}>
            <span className="eyebrow">Search</span>
            <input
              type="search"
              value={typed}
              placeholder={data?.dict.pt ? "visit, facility, payer, patient" : "visit, facility, payer"}
              onChange={(e) => setTyped(e.target.value)}
              style={{
                fontFamily: 'var(--sans)',
                fontSize: 'var(--t-table)',
                padding: '5px 7px',
                borderRadius: 6,
                border: '1px solid var(--rule-2)',
                background: 'var(--surface)',
                color: 'var(--ink)',
              }}
            />
          </label>
          <button
            type="button"
            className="reset-btn"
            style={{ marginLeft: 'auto' }}
            onClick={() => {
              setPicked(NONE)
              setTeam(null)
              setDept(null)
              setHome(null)
              setGroup('')
              setOnly([])
              setResolvedIn([])
              setVisits(null)
              setTyped('')
              setQ('')
            }}
          >
            Clear
          </button>
        </div>
      }
      summary={
        data && (
          <>
            {int(rows.length)} claims of {int(data.n)} on file
            {visits && (
              <>
                {' · '}
                <span className="pill-sel" data-testid="claim-visits">
                  {visits.label}: {int(visits.keys.length)} claims
                  <button type="button" title="Clear" onClick={() => setVisits(null)}>
                    ×
                  </button>
                </span>
                {missing > 0 && (
                  <span style={{ color: 'var(--ink-3)' }}>
                    {' '}
                    · {int(missing)} not in Claim detail (billed and resolved outside its months, or not yours to see)
                  </span>
                )}
              </>
            )}
            {outside.length > 0 && (
              <>
                {' · '}
                <span data-testid="claim-search-outside">
                  Not in your current filters: {int(outside.length)} {outside.length === 1 ? 'match' : 'matches'} outside.{' '}
                  <button type="button" className="cell-link" onClick={showOutside}>
                    Show
                  </button>
                </span>
              </>
            )}
            {weighted !== null && weighted !== rows.length && <> · {weighted.toLocaleString('en-US', { maximumFractionDigits: 1 })} weighted</>}{withLabor && <> · {moneyK(labor)} labor</>}
            {hidden > 0 && (
              <>
                {' · '}
                <HiddenByStatus count={hidden} noun="claims" />
              </>
            )}
            {team && (
              <>
                {' · '}
                <span className="pill-sel">
                  {team.label}
                  <button type="button" title="Clear" onClick={() => setTeam(null)}>
                    ×
                  </button>
                </span>
              </>
            )}
            {dept !== null && (
              <>
                {' · '}
                <span className="pill-sel">
                  Department: {data.dict.dept[dept]}
                  <button type="button" title="Clear" onClick={() => setDept(null)}>
                    ×
                  </button>
                </span>
              </>
            )}
            {only.length > 0 && (
              <>
                {' · '}
                <span className="pill-sel" data-testid="claim-months">
                  Billed in {monthRuns(only)}
                  <button type="button" title="Back to the top bar's months" onClick={() => setOnly([])}>
                    ×
                  </button>
                </span>
              </>
            )}
            {byResolution && (
              <>
                {' · '}
                <span className="pill-sel" data-testid="claim-resolved-in">
                  Resolved in {monthRuns(resolvedIn)}{only.length ? '' : ' · any bill month'}
                  <button type="button" title="Any resolution month" onClick={() => setResolvedIn([])}>
                    ×
                  </button>
                </span>
              </>
            )}
            {home !== null && (
              <>
                {' · '}
                <span className="pill-sel">
                  Home department: {data.dict.wd?.[home]}
                  <button type="button" title="Clear" onClick={() => setHome(null)}>
                    ×
                  </button>
                </span>
              </>
            )}
          </>
        )
      }
      rows={rows}
      columns={columns}
      exportColumns={exportColumns}
      exportName="claim-detail.csv"
      rowKey={(i) => String(i)}
      message={error ?? (data ? undefined : 'Loading claims…')}
    />
    </>
  )
}
